/**
 * API Key Service
 *
 * Service for managing API keys for external API access.
 */

import api from './api';
import type {
  ApiKeyDisplay,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  UpdateApiKeyRequest,
  ApiKeyScope,
} from '@budgira/shared';

// Re-export for convenience
export type { ApiKeyDisplay, ApiKeyScope };

interface ApiKeyListResponse {
  success: boolean;
  data: ApiKeyDisplay[];
}

interface ApiKeyDetailResponse {
  success: boolean;
  data: ApiKeyDisplay;
}

interface ApiKeyCreateResponse {
  success: boolean;
  data: CreateApiKeyResponse;
  warning: string;
}

interface ApiKeyUpdateResponse {
  success: boolean;
  data: ApiKeyDisplay;
}

interface ApiKeyActionResponse {
  success: boolean;
  message: string;
}

/**
 * Get all API keys for the current user
 */
export async function listApiKeys(): Promise<ApiKeyDisplay[]> {
  const response = await api.get<ApiKeyListResponse>('/api-keys');
  return response.data.data;
}

/**
 * Get a specific API key
 */
export async function getApiKey(id: string): Promise<ApiKeyDisplay> {
  const response = await api.get<ApiKeyDetailResponse>(`/api-keys/${id}`);
  return response.data.data;
}

/**
 * Create a new API key
 * Returns the key value only once - it cannot be retrieved later
 */
export async function createApiKey(
  data: CreateApiKeyRequest
): Promise<{ apiKey: ApiKeyDisplay; key: string; warning: string }> {
  const response = await api.post<ApiKeyCreateResponse>('/api-keys', data);
  return {
    apiKey: response.data.data.apiKey as ApiKeyDisplay,
    key: response.data.data.key,
    warning: response.data.warning,
  };
}

/**
 * Update an API key
 */
export async function updateApiKey(
  id: string,
  data: UpdateApiKeyRequest
): Promise<ApiKeyDisplay> {
  const response = await api.patch<ApiKeyUpdateResponse>(`/api-keys/${id}`, data);
  return response.data.data;
}

/**
 * Revoke an API key
 */
export async function revokeApiKey(id: string): Promise<void> {
  await api.post<ApiKeyActionResponse>(`/api-keys/${id}/revoke`);
}

/**
 * Delete an API key permanently
 */
export async function deleteApiKey(id: string): Promise<void> {
  await api.delete<ApiKeyActionResponse>(`/api-keys/${id}`);
}
